/**
 * gradeStore — localStorage-based grade storage.
 *
 * Schema per entry:
 *   key  : `ep_grade_{groupId}_{taskId}_{studentId}`
 *   value: GradeEntry (JSON)
 *
 * Will be replaced by proper API calls once backend endpoint is confirmed.
 */

export type GradeScale = 'five' | 'ten' | 'hundred' | 'percent' | 'custom';

/** One level in a custom scale */
export interface CustomLevel {
  label: string;  // e.g. "A", "Отл", "C1"
  minPct: number; // minimum percent (0-100) to achieve this level
}

/** Full custom scale definition for a group */
export interface CustomScaleConfig {
  name: string;          // e.g. "Cambridge", "Олимпийская"
  levels: CustomLevel[]; // sorted descending by minPct when used
}

export interface GradeEntry {
  /** Raw percent 0–100 */
  pct: number;
  /** Converted value on the chosen scale */
  grade: number;
  scale: GradeScale;
  attemptNumber: number;
  completedAt: string; // ISO
  /** Source trainer type */
  trainerType: 'test' | 'treadmill' | 'exercise' | 'manual';
  /** Optional teacher override */
  manualGrade?: string;
  missed?: boolean;
  outstanding?: boolean;
  /** Audio URLs from student speaking/recording answers (uploaded blobs) */
  audioUrls?: string[];
  /** Teacher feedback comment (saved to Parse Server via TeacherGrade class) */
  teacherComment?: string;
}

/* ── Scale conversion ──────────────────────────────────────────── */

export function pctToGrade(pct: number, scale: GradeScale): number {
  switch (scale) {
    case 'percent':  return Math.round(pct);
    case 'hundred':  return Math.round(pct);
    case 'ten':      return +(pct / 10).toFixed(1);
    case 'custom':   return Math.round(pct); // raw pct stored; display via pctToCustomLabel
    case 'five':
    default: {
      if (pct >= 90) return 5;
      if (pct >= 75) return 4;
      if (pct >= 55) return 3;
      if (pct >= 35) return 2;
      return 2;
    }
  }
}

/** Convert percent to a custom level label */
export function pctToCustomLabel(pct: number, config: CustomScaleConfig): string {
  const sorted = [...config.levels].sort((a, b) => b.minPct - a.minPct);
  for (const level of sorted) {
    if (pct >= level.minPct) return level.label;
  }
  return sorted[sorted.length - 1]?.label ?? '—';
}

export function gradeLabel(entry: GradeEntry): string {
  if (entry.manualGrade) return entry.manualGrade;
  switch (entry.scale) {
    case 'five':
    case 'ten':     return String(entry.grade);
    case 'percent':
    case 'hundred': return `${entry.grade}%`;
    case 'custom':  return `${entry.grade}%`;
    default:        return String(entry.grade);
  }
}

/* ── Storage key ───────────────────────────────────────────────── */

function key(groupId: string, taskId: string, studentId: string) {
  return `ep_grade_${groupId}_${taskId}_${studentId}`;
}

/* ── Public API ────────────────────────────────────────────────── */

export const GradeStore = {
  get(groupId: string, taskId: string, studentId: string): GradeEntry | null {
    try {
      const raw = localStorage.getItem(key(groupId, taskId, studentId));
      return raw ? (JSON.parse(raw) as GradeEntry) : null;
    } catch { return null; }
  },

  set(groupId: string, taskId: string, studentId: string, entry: GradeEntry): void {
    try {
      localStorage.setItem(key(groupId, taskId, studentId), JSON.stringify(entry));
    } catch { /* storage full — silent */ }
  },

  /** Save auto-grade result from a completed trainer.
   *  Saves to Parse Server (primary) and localStorage (cache/fallback).
   *  Retries up to 3 times with exponential back-off.
   *  If all retries fail → queues the save for next app launch + shows toast.
   *  Pass trainerId to store the grade per-trainer so individual trainers can be reset. */
  saveAutoGrade(opts: {
    groupId: string;
    taskId: string;
    studentId: string;
    pct: number;
    scale: GradeScale;
    trainerType: 'test' | 'treadmill' | 'exercise';
    /** Specific trainer instance ID (e.g. exercise refId) for per-trainer grade isolation */
    trainerId?: string;
    attemptNumber?: number;
  }) {
    const existing = GradeStore.get(opts.groupId, opts.taskId, opts.studentId);
    const attempt = (existing?.attemptNumber ?? 0) + 1;
    const entry: GradeEntry = {
      pct: opts.pct,
      grade: pctToGrade(opts.pct, opts.scale),
      scale: opts.scale,
      attemptNumber: opts.attemptNumber ?? attempt,
      completedAt: new Date().toISOString(),
      trainerType: opts.trainerType,
    };
    // ① Local cache — instant, always works
    GradeStore.set(opts.groupId, opts.taskId, opts.studentId, entry);
    // ② Server — with retry + fallback queue
    if (opts.groupId && opts.taskId && opts.studentId) {
      saveToServerWithRetry({
        groupId:     opts.groupId,
        taskId:      opts.taskId,
        studentId:   opts.studentId,
        trainerType: opts.trainerType,
        trainerId:   opts.trainerId,
        pct:         opts.pct,
      });
    }
    return entry;
  },


  /** Manual teacher override (from Ledger popup or GradePanel).
   *  Saves to localStorage (instant) and to server (fire-and-forget). */
  saveManual(groupId: string, taskId: string, studentId: string, patch: {
    manualGrade?: string;
    missed?: boolean;
    outstanding?: boolean;
    scale?: GradeScale;
    teacherComment?: string;
    /** Optional: specific trainer ID being graded */
    trainerId?: string;
  }) {
    const existing = GradeStore.get(groupId, taskId, studentId) ?? {
      pct: 0, grade: 0, scale: patch.scale ?? 'five' as GradeScale,
      attemptNumber: 0, completedAt: new Date().toISOString(), trainerType: 'manual' as const,
    };
    const entry: GradeEntry = {
      ...existing,
      ...patch,
      trainerType: existing.trainerType === 'manual' ? 'manual' : existing.trainerType,
    };
    // ① Local cache — instant
    GradeStore.set(groupId, taskId, studentId, entry);
    // ② Server — fire-and-forget
    if (groupId && taskId && studentId) {
      import('@/api/grades.api').then(({ TeacherGradesAPI }) => {
        TeacherGradesAPI.save({
          groupId, taskId, studentId,
          trainerId: patch.trainerId ?? '',
          manualGrade: patch.manualGrade ?? '',
          comment: patch.teacherComment ?? '',
          missed:       patch.missed       ?? false,
          outstanding:  patch.outstanding  ?? false,
          scale:        patch.scale        ?? (existing.scale ?? 'five'),
        }).catch(() => { /* silent — teacher saves also happen in TaskAssessmentView */ });
      }).catch(() => {});
    }
    return entry;
  },

  /** Get all grades for a group (for ledger) */
  getAllForGroup(groupId: string): Record<string, GradeEntry> {
    const prefix = `ep_grade_${groupId}_`;
    const result: Record<string, GradeEntry> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) {
        try {
          const raw = localStorage.getItem(k);
          if (raw) result[k.slice(prefix.length)] = JSON.parse(raw);
        } catch { /* skip */ }
      }
    }
    return result;
  },

  /** Get attempt count for a specific trainer (not the task) */
  getAttemptCount(groupId: string, taskId: string, studentId: string): number {
    return GradeStore.get(groupId, taskId, studentId)?.attemptNumber ?? 0;
  },
  /** Append an audio URL to the grade entry (student speaking answer upload) */
  appendAudioUrl(groupId: string, taskId: string, studentId: string, url: string): void {
    const existing = GradeStore.get(groupId, taskId, studentId) ?? {
      pct: 0, grade: 0, scale: 'five' as GradeScale,
      attemptNumber: 0, completedAt: new Date().toISOString(), trainerType: 'manual' as const,
    };
    const entry: GradeEntry = {
      ...existing,
      audioUrls: [...(existing.audioUrls ?? []), url],
    };
    GradeStore.set(groupId, taskId, studentId, entry);
  },

  /** Remove a student's grade from localStorage (called after server-side deletion). */
  clearGrade(groupId: string, taskId: string, studentId: string) {
    try { localStorage.removeItem(key(groupId, taskId, studentId)); } catch { /* ignore */ }
  },

  /**
   * Reset grade for ONE specific trainer without wiping the whole task entry.
   * Sets pct/grade to 0 so the task shows 0% until the student retakes this trainer.
   * Other trainers' grades within the same task are unaffected.
   */
  clearTrainerGrade(groupId: string, taskId: string, studentId: string, _trainerId: string) {
    try {
      const existing = GradeStore.get(groupId, taskId, studentId);
      if (!existing) return; // nothing to clear
      const reset: GradeEntry = {
        ...existing,
        pct: 0,
        grade: 0,
        attemptNumber: 0,
        completedAt: new Date().toISOString(),
        audioUrls: undefined,
        manualGrade: undefined,
      };
      GradeStore.set(groupId, taskId, studentId, reset);
    } catch { /* ignore */ }
  },
};

/** Get default scale for a group — stored in localStorage per group */
export function getGroupScale(groupId: string): GradeScale {
  const raw = localStorage.getItem(`ep_scale_${groupId}`);
  return (raw as GradeScale | null) ?? 'five';
}

export function setGroupScale(groupId: string, scale: GradeScale) {
  localStorage.setItem(`ep_scale_${groupId}`, scale);
}

/** Custom scale config (only used when scale === 'custom') */
export function getCustomScale(groupId: string): CustomScaleConfig {
  try {
    const raw = localStorage.getItem(`ep_custom_scale_${groupId}`);
    if (raw) return JSON.parse(raw) as CustomScaleConfig;
  } catch { /* ignore */ }
  // Default starter config
  return {
    name: 'Моя шкала',
    levels: [
      { label: 'A', minPct: 90 },
      { label: 'B', minPct: 75 },
      { label: 'C', minPct: 55 },
      { label: 'D', minPct: 35 },
      { label: 'F', minPct: 0  },
    ],
  };
}

export function setCustomScale(groupId: string, config: CustomScaleConfig) {
  localStorage.setItem(`ep_custom_scale_${groupId}`, JSON.stringify(config));
}

/* ── Server-save retry helpers ─────────────────────────────────── */

const RETRY_QUEUE_KEY = 'ep_grade_retry_queue';

interface PendingGradeSave {
  groupId: string;
  taskId: string;
  studentId: string;
  trainerType: 'test' | 'treadmill' | 'exercise';
  trainerId?: string;
  pct: number;
  savedAt: number; // epoch ms — for expiry (discard after 7 days)
}

/** Attempt to save a grade to the server with exponential back-off.
 *  If all retries fail → enqueue for next app launch + show toast. */
export async function saveToServerWithRetry(
  opts: Omit<PendingGradeSave, 'savedAt'>,
  maxRetries = 3,
  baseDelayMs = 2000,
): Promise<void> {
  const { default: GradesAPI } = await import('@/api/grades.api');
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await GradesAPI.saveAttempt(opts);
      return; // success — done
    } catch (err) {
      lastError = err;
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, baseDelayMs * 2 ** attempt));
      }
    }
  }

  // All retries exhausted — warn student and queue for later
  console.warn('[gradeStore] saveAttempt failed after retries, queuing:', lastError);

  // Enqueue in localStorage
  try {
    const raw = localStorage.getItem(RETRY_QUEUE_KEY);
    const queue: PendingGradeSave[] = raw ? JSON.parse(raw) : [];
    queue.push({ ...opts, savedAt: Date.now() });
    // Keep at most 100 pending saves; drop oldest
    localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(queue.slice(-100)));
  } catch { /* storage full — ignore */ }

  // Show non-blocking toast (lazy import to avoid circular dep)
  try {
    const { default: toast } = await import('react-hot-toast');
    toast(
      '⚠️ Результат сохранён локально. Отправим на сервер при следующем подключении.',
      {
        duration: 6000,
        style: {
          background: '#fef3c7',
          color: '#92400e',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        icon: '📶',
      }
    );
  } catch { /* toast unavailable */ }
}

/**
 * Call this once on app startup (e.g. in App.tsx useEffect).
 * Drains the localStorage retry queue — sends any grades that failed
 * during a previous session to the server.
 */
export async function processGradeRetryQueue(): Promise<void> {
  try {
    const raw = localStorage.getItem(RETRY_QUEUE_KEY);
    if (!raw) return;
    const queue: PendingGradeSave[] = JSON.parse(raw);
    if (!queue.length) return;

    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const fresh = queue.filter(item => item.savedAt > sevenDaysAgo);
    if (!fresh.length) {
      localStorage.removeItem(RETRY_QUEUE_KEY);
      return;
    }

    const { default: GradesAPI } = await import('@/api/grades.api');
    const remaining: PendingGradeSave[] = [];

    for (const item of fresh) {
      try {
        await GradesAPI.saveAttempt(item);
        console.log('[gradeStore] queued grade synced:', item.taskId, item.pct + '%');
      } catch {
        remaining.push(item); // still failing — keep for next time
      }
    }

    if (remaining.length === 0) {
      localStorage.removeItem(RETRY_QUEUE_KEY);
    } else {
      localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(remaining));
    }
  } catch (e) {
    console.warn('[gradeStore] processGradeRetryQueue error:', e);
  }
}
